// Loceng notifikasi dalam aplikasi (Prompt D.A.5)
// Bell di topbar + panel dropdown + badge belum dibaca + tandai dibaca.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, FileCheck2, FileX2, ReceiptText, AlertTriangle, Info } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';

const JENIS_ICON = {
  proof_lulus: FileCheck2, proof_tolak: FileX2, bil_baru: ReceiptText, tunggakan: AlertTriangle, info: Info,
};
const JENIS_COLOR = {
  proof_lulus: 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/60',
  proof_tolak: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/60',
  bil_baru: 'text-brand bg-brand-light',
  tunggakan: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/60',
  info: 'text-muted-foreground bg-muted',
};

function masaLalu(s, t) {
  const d = Date.now() - new Date(s).getTime();
  const m = Math.floor(d / 60000);
  if (m < 1) return t('baruSahaja');
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}j`;
  return `${Math.floor(h / 24)}h`;
}

export default function NotifBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('notifications')
      .select('id, tajuk, mesej, jenis, pautan, dibaca, created_at')
      .order('created_at', { ascending: false }).limit(20);
    setItems(data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 120000); // poll setiap 2 minit
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const unread = items.filter((i) => !i.dibaca).length;

  const markOne = async (n) => {
    if (!n.dibaca) {
      setItems((xs) => xs.map((x) => (x.id === n.id ? { ...x, dibaca: true } : x)));
      await supabase.from('notifications').update({ dibaca: true }).eq('id', n.id);
    }
    setOpen(false);
    if (n.pautan) navigate(n.pautan);
  };

  const markAll = async () => {
    setItems((xs) => xs.map((x) => ({ ...x, dibaca: true })));
    await supabase.from('notifications').update({ dibaca: true }).eq('dibaca', false);
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => { setOpen((o) => !o); if (!open) load(); }}
        className="relative h-11 w-11 flex items-center justify-center rounded-lg hover:bg-muted transition-colors"
        data-testid="notif-bell" aria-label={t('notifikasi')}>
        <Bell className="h-5 w-5 text-muted-foreground" />
        {unread > 0 && (
          <span className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[340px] max-w-[90vw] bg-card border border-border rounded-2xl shadow-xl z-50 overflow-hidden" data-testid="notif-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <p className="font-bold text-sm text-foreground">{t('notifikasi')}</p>
            {unread > 0 && (
              <button onClick={markAll} className="text-xs font-semibold text-brand hover:underline flex items-center gap-1">
                <CheckCheck className="h-3.5 w-3.5" />{t('tandaiDibacaSemua')}
              </button>
            )}
          </div>
          <div className="max-h-[380px] overflow-y-auto">
            {loading && items.length === 0 && <p className="p-4 text-sm text-muted-foreground text-center">{t('memuat')}</p>}
            {!loading && items.length === 0 && <p className="p-6 text-sm text-muted-foreground text-center">{t('tiadaNotifikasi')}</p>}
            {items.map((n) => {
              const Icon = JENIS_ICON[n.jenis] || Info;
              return (
                <button key={n.id} onClick={() => markOne(n)}
                  className={`w-full text-left px-4 py-3 border-b border-border hover:bg-muted transition-colors ${n.dibaca ? '' : 'bg-brand-light/40'}`}>
                  <div className="flex gap-3">
                    <span className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 ${JENIS_COLOR[n.jenis] || JENIS_COLOR.info}`}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm ${n.dibaca ? 'text-muted-foreground' : 'font-bold text-foreground'}`}>{n.tajuk}</p>
                      {n.mesej && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.mesej}</p>}
                      <p className="text-[11px] text-muted-foreground mt-1">{masaLalu(n.created_at, t)}</p>
                    </div>
                    {!n.dibaca && <span className="h-2 w-2 rounded-full bg-brand mt-1.5 shrink-0" />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
